import { SerializableSchema, z } from "@repo/utils";
import { OrganizationIdSchema } from "../organization/z.js";
import { InboxIdSchema, MessageIdSchema, ThreadIdSchema } from "../inbox/z.js";
import { EntityIdSchema } from "@repo/core/universal";
import { PositionIdSchema, PositionMatchIdSchema, TransactionIdSchema } from "./results/z.js";

export const WorkflowStatusSchema = z.enum(["STARTED", "IN_PROGRESS", "PROCESSED", "FAILED", "REPLACED"]);
export const ErpSystemSchema = z.enum([
    "ODOO",
    "DRESSELHAUS_SAP",
    "FILE_EXPORT",
    "SAP_B1",
    "NETSUITE",
    "EPICOR",
    "ORDERWISE",
    "REFLEX_SAP",
    "HAUFE_X360",
    "SCHELLING",
    "WASHTEC_SAP",
    "SUCO",
    "GEHR",
    "UTZ",
    "ASATEX",
    "NONE",
]);
export const DocumentSystemSchema = z.enum(["ODOO", "FILE_EXPORT", "SAP_B1", "DOCUWARE", "FORM_DATA", "UTZ", "NONE"]);
export const EmailSystemSchema = z.enum(["ODOO", "FILE_EXPORT", "SAP_B1", "DOCUWARE", "FORM_DATA", "UTZ", "NONE"]);
export const DeliveryTypeSchema = z.enum(["DELIVERY", "PICKUP"]);

export const ClassificationSchema = z.enum([
    "ORDER",
    "REQUEST_FOR_PROPOSAL",
    "REQUEST_FOR_QUOTATION",
    "QUESTION_ABOUT_ONGOING",
    "CALL_OFF_ORDER",
    "ADJUST_ALREADY_PLACED_ORDER",
    "OTHER",
]);

// BASE SCHEMAS
/**
 * Standard audit fields present in all sales order entities
 */
export const AuditFieldsSchema = z.object({
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
/**
 * Base schema for all syncable sales order entities
 */
export const SyncableEntitySchema = z.object({
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    orgId: z.string(),
    syncMarker: z.nullish(z.string()),
});
/**
 * Standard defaults for audit fields in upsert operations
 */
export const UpsertAuditDefaults = {
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
};
/**
 * Base schema for upsert operations on syncable entities
 */
export const UpsertSyncableEntitySchema = z.object({
    orgId: z.string(),
    syncMarker: z.nullish(z.string()),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// CONTACTS

export const ContactSchema = z.object({
    orgId: z.string(),
    id: z.string(),
    contactNumber: z.nullish(z.string()),
    name: z.string(),
    phone1: z.nullish(z.string()),
    phone2: z.nullish(z.string()),
    phone3: z.nullish(z.string()),
    email1: z.nullish(z.string()),
    email2: z.nullish(z.string()),
    email3: z.nullish(z.string()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    deletedAt: z.nullish(z.string()),
    blocked: z.nullish(z.boolean()),
    blockedAt: z.nullish(z.string()),
    syncMarker: z.nullish(z.string()),
});

export const UpsertContactSchema = z.object({
    id: z.string().check(z.minLength(1)),
    contactNumber: z._default(z.nullish(z.string()), null),
    name: z.string(),
    phone1: z._default(z.nullish(z.string()), null),
    phone2: z._default(z.nullish(z.string()), null),
    phone3: z._default(z.nullish(z.string()), null),
    email1: z._default(z.nullish(z.string()), null),
    email2: z._default(z.nullish(z.string()), null),
    email3: z._default(z.nullish(z.string()), null),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// PARTNER CONTACTS

export const PartnerContactSchema = z.object({
    orgId: z.string(),
    contactId: z.string(),
    partnerId: z.string(),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    deletedAt: z.nullish(z.string()),
    blocked: z.nullish(z.boolean()),
    blockedAt: z.nullish(z.string()),
    syncMarker: z.nullish(z.string()),
});

export const UpsertPartnerContactSchema = z.object({
    contactId: z.string().check(z.minLength(1)),
    partnerId: z.string().check(z.minLength(1)),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// SALES ORDER WORKFLOW

export const WorkflowIdSchema = EntityIdSchema("wkf");
export const WorkflowSchema = z.object({
    id: WorkflowIdSchema,
    invalidated: z.boolean(),
    orgId: OrganizationIdSchema,
    threadId: ThreadIdSchema,
    inboxId: InboxIdSchema,
    status: WorkflowStatusSchema,
    statusDetails: z.nullish(SerializableSchema),
    finishedAt: z.nullish(z.string()),
    messageIds: z.array(z.string()),
    attachmentIds: z.array(z.string()),
    unsupportedAttachmentIds: z.array(z.string()),
    unsupportedAttachmentNames: z.array(z.string()),
    errorAttachmentIds: z.array(z.string()),
    errorAttachmentNames: z.array(z.string()),
    detectedTransactions: z.nullish(SerializableSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    classification: z.nullish(z.string()),
});
export const ProcessedMessageAndAttachmentIdsSchema = z.object({
    messageIds: z.array(z.string()),
    attachmentIds: z.array(z.string()),
});
export const SalesOrderWorkflowSchema = WorkflowSchema;
export const UpsertWorkflowSchema = z.object({
    id: WorkflowIdSchema,
    orgId: OrganizationIdSchema,
    threadId: ThreadIdSchema,
    inboxId: InboxIdSchema,
    status: WorkflowStatusSchema,
    statusDetails: z._default(z.nullish(SerializableSchema), null),
    finishedAt: z._default(z.nullish(z.string()), null),
    messageIds: z._default(z.array(z.string()), []),
    attachmentIds: z._default(z.array(z.string()), []),
    unsupportedAttachmentIds: z.array(z.string()),
    unsupportedAttachmentNames: z.array(z.string()),
    errorAttachmentIds: z.array(z.string()),
    errorAttachmentNames: z.array(z.string()),
    detectedTransactions: z._default(z.nullish(SerializableSchema), null),
});
export const CreateWorkflowSchema = z.object({
    id: WorkflowIdSchema,
    orgId: OrganizationIdSchema,
    threadId: ThreadIdSchema,
    inboxId: InboxIdSchema,
    messageIds: z._default(z.array(z.string()), []),
    attachmentIds: z._default(z.array(z.string()), []),
    unsupportedAttachmentIds: z._default(z.array(z.string()), []),
    unsupportedAttachmentNames: z._default(z.array(z.string()), []),
    errorAttachmentIds: z._default(z.array(z.string()), []),
    errorAttachmentNames: z._default(z.array(z.string()), []),
});
export const UpdateWorkflowStatusSchema = z.object({
    id: WorkflowIdSchema,
    statusDetails: z._default(z.nullish(SerializableSchema), null),
});
export const UpdateWorkflowSchema = z.object({
    id: WorkflowIdSchema,
    detectedTransactions: z._default(z.nullish(SerializableSchema), null),
    classification: z.nullish(z.string()),
});
export const FinishWorkflowSchema = z.object({
    id: WorkflowIdSchema,
    statusDetails: z._default(z.nullish(SerializableSchema), null),
});
export const FailWorkflowSchema = z.object({
    id: WorkflowIdSchema,
    error: z.unknown(),
});

// SALES ORDER PIPELINE RESULTS

export const ExtractedAddressSchema = z.object({
    name1: z.string(),
    name2: z.nullable(z.string()),
    name3: z.nullable(z.string()),
    street1: z.string(),
    houseNumber1: z.string(),
    streetWithNumber1: z.string(),
    street2: z.nullable(z.string()),
    houseNumber2: z.nullable(z.string()),
    streetWithNumber2: z.nullable(z.string()),
    street3: z.nullable(z.string()),
    houseNumber3: z.nullable(z.string()),
    streetWithNumber3: z.nullable(z.string()),
    deliveryInstructions: z.nullish(z.array(z.string())),
    postalCode: z.string(),
    postBox: z.nullable(z.string()),
    city: z.string(),
    county: z.nullable(z.string()),
    countryCode: z.string(),
});

export const SuggestedTransactionSchema = z.object({
    id: TransactionIdSchema,
    relevantMessageIds: z._default(z.array(z.string()), []),
    relevantAttachmentIds: z._default(z.array(z.string()), []),
    customerAccountIds: z._default(z.array(z.string()), []),
    billToPartyIds: z._default(z.array(z.string()), []),
    shipToPartyIds: z._default(z.array(z.string()), []),
    sellingEntityId: z._default(z.nullish(z.string()), null),
    sellingEntityAddressId: z._default(z.nullish(z.string()), null),
    newBilledPartyAddress: z._default(z.nullish(ExtractedAddressSchema), null),
    newPayingPartyAddress: z._default(z.nullish(ExtractedAddressSchema), null),
    newShipToAddress: z._default(z.nullish(ExtractedAddressSchema), null),
    shippingTerms: z._default(z.nullish(z.string()), null),
    paymentTerms: z._default(z.nullish(z.string()), null),
    receivedDate: z.string(),
    emailReceivedDate: z._default(z.nullish(z.string()), null),
    orderDate: z.string(),
    deliveryType: z._default(z.nullish(DeliveryTypeSchema), null),
    contactId: z.nullish(z.string()),
    contactName: z.string(),
    contactEmail: z.string(),
    contactPhone: z._default(z.nullish(z.string()), null),
    orderReference: z._default(z.nullish(z.string()), null),
    quoteNumber: z._default(z.nullish(z.string()), null),
    transactionType: z.string(),
    eligibleOrderSplitStrategy: z._default(z.nullish(z.string()), null),
    appliedOrderSplitStrategy: z._default(z.nullish(z.string()), null),
    currencyId: z.string(),
    currencyCode: z.string(),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    orgId: z.string(),
    threadId: ThreadIdSchema,
    workflowId: WorkflowIdSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    invalidated: z.boolean(),
    cancelled: z._default(z.boolean(), false),
    cancelledBy: z._default(z.nullable(z.string()), null),
    cancelledAt: z._default(z.nullable(z.string()), null),
    cancelReason: z._default(z.nullable(z.string()), null),
    autoSubmitResult: z._default(z.nullable(z.enum(["SUCCESS", "FAILURE"])), null),
    autoSubmitError: z._default(z.nullable(z.string()), null),
    autoSubmittedAt: z._default(z.nullable(z.string()), null),
});
export const UpsertSuggestedTransactionSchema = z.object({
    id: TransactionIdSchema,
    orgId: OrganizationIdSchema,
    threadId: ThreadIdSchema,
    workflowId: WorkflowIdSchema,
    relevantMessageIds: z._default(z.array(z.string()), []),
    relevantAttachmentIds: z._default(z.array(z.string()), []),
    customerAccountIds: z._default(z.array(z.string()), []),
    billToPartyIds: z._default(z.array(z.string()), []),
    shipToPartyIds: z._default(z.array(z.string()), []),
    sellingEntityId: z._default(z.nullish(z.string()), null),
    sellingEntityAddressId: z._default(z.nullish(z.string()), null),
    newBilledPartyAddress: z._default(z.nullish(SerializableSchema), null),
    newPayingPartyAddress: z._default(z.nullish(SerializableSchema), null),
    newShipToAddress: z._default(z.nullish(SerializableSchema), null),
    shippingTerms: z._default(z.nullish(z.string()), null),
    paymentTerms: z._default(z.nullish(z.string()), null),
    receivedDate: z.string(),
    emailReceivedDate: z._default(z.nullish(z.string()), null),
    orderDate: z.string(),
    deliveryType: z._default(z.nullish(DeliveryTypeSchema), null),
    contactId: z._default(z.nullish(z.string()), null),
    contactName: z.string(),
    contactEmail: z.string(),
    contactPhone: z._default(z.nullish(z.string()), null),
    orderReference: z._default(z.nullish(z.string()), null),
    quoteNumber: z._default(z.nullish(z.string()), null),
    transactionType: z.string(),
    eligibleOrderSplitStrategy: z._default(z.nullish(z.string()), null),
    appliedOrderSplitStrategy: z._default(z.nullish(z.string()), null),
    currencyId: z.string(),
    currencyCode: z.string(),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});
export const SuggestedPositionSchema = z.object({
    id: PositionIdSchema,
    invalidated: z.boolean(),
    positionIndex: z.number(),
    extractedPositionIndex: z._default(z.nullish(z.string()), null),
    deliveryDate: z._default(z.nullish(z.string()), null),
    extractedDeliveryDate: z._default(z.nullish(z.string()), null),
    extractedArticleNumbers: z._default(z.nullish(z.array(z.string())), null),
    extractedDescription: z.string(),
    extractedQuantity1: z.number(),
    extractedQuantityUnit1: z.string(),
    extractedQuantity2: z._default(z.nullish(z.number()), null),
    extractedQuantityUnit2: z._default(z.nullish(z.string()), null),
    extractedQuantity3: z._default(z.nullish(z.number()), null),
    extractedQuantityUnit3: z._default(z.nullish(z.string()), null),
    extractedPrice1: z._default(z.nullish(z.number()), null),
    extractedPriceUnit1: z._default(z.nullish(z.string()), null),
    extractedPriceQuantity1: z._default(z.nullish(z.number()), null),
    extractedPrice2: z._default(z.nullish(z.number()), null),
    extractedPriceUnit2: z._default(z.nullish(z.string()), null),
    extractedPriceQuantity2: z._default(z.nullish(z.number()), null),
    extractedPrice3: z._default(z.nullish(z.number()), null),
    extractedPriceUnit3: z._default(z.nullish(z.string()), null),
    extractedPriceQuantity3: z._default(z.nullish(z.number()), null),
    extractedTotalValue: z._default(z.nullish(z.number()), null),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    orgId: z.string(),
    threadId: z.string(),
    transactionId: TransactionIdSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const UpsertSuggestedPositionSchema = z.object({
    id: PositionIdSchema,
    orgId: OrganizationIdSchema,
    threadId: ThreadIdSchema,
    transactionId: TransactionIdSchema,
    positionIndex: z.number(),
    extractedPositionIndex: z._default(z.nullish(z.string()), null),
    deliveryDate: z._default(z.nullish(z.string()), null),
    extractedDeliveryDate: z._default(z.nullish(z.string()), null),
    extractedArticleNumbers: z._default(z.nullish(z.array(z.string())), null),
    extractedDescription: z.string(),
    extractedQuantity1: z.number(),
    extractedQuantityUnit1: z.string(),
    extractedQuantity2: z._default(z.nullish(z.number()), null),
    extractedQuantityUnit2: z._default(z.nullish(z.string()), null),
    extractedQuantity3: z._default(z.nullish(z.number()), null),
    extractedQuantityUnit3: z._default(z.nullish(z.string()), null),
    extractedPrice1: z._default(z.nullish(z.number()), null),
    extractedPriceUnit1: z._default(z.nullish(z.string()), null),
    extractedPriceQuantity1: z._default(z.nullish(z.number()), null),
    extractedPrice2: z._default(z.nullish(z.number()), null),
    extractedPriceUnit2: z._default(z.nullish(z.string()), null),
    extractedPriceQuantity2: z._default(z.nullish(z.number()), null),
    extractedPrice3: z._default(z.nullish(z.number()), null),
    extractedPriceUnit3: z._default(z.nullish(z.string()), null),
    extractedPriceQuantity3: z._default(z.nullish(z.number()), null),
    extractedTotalValue: z._default(z.nullish(z.number()), null),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});
export const SuggestedPositionMatchSchema = z.object({
    id: PositionMatchIdSchema,
    invalidated: z.boolean(),
    productId: z.string(),
    product: SerializableSchema,
    quantity: z.number(),
    validQuantity: z.number(),
    quantityUnitId: z.string(),
    quantityUnitCode: z.string(),
    price: z._default(z.nullish(z.number()), null),
    priceUnitId: z._default(z.nullish(z.string()), null),
    priceUnitCode: z._default(z.nullish(z.string()), null),
    priceQuantity: z._default(z.nullish(z.number()), null),
    matchingWarning: z._default(z.boolean(), false),
    matchingExplanation: z._default(z.nullish(z.string()), null),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    orgId: z.string(),
    threadId: z.string(),
    positionId: PositionIdSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const UpsertSuggestedPositionMatchSchema = z.object({
    id: PositionMatchIdSchema,
    orgId: OrganizationIdSchema,
    threadId: ThreadIdSchema,
    positionId: PositionIdSchema,
    productId: z.string(),
    product: SerializableSchema,
    quantity: z.number(),
    validQuantity: z.number(),
    quantityUnitId: z.string(),
    quantityUnitCode: z.string(),
    price: z._default(z.nullish(z.number()), null),
    priceUnitId: z._default(z.nullish(z.string()), null),
    priceUnitCode: z._default(z.nullish(z.string()), null),
    priceQuantity: z._default(z.nullish(z.number()), null),
    matchingWarning: z._default(z.boolean(), false),
    matchingExplanation: z._default(z.nullish(z.string()), null),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// PRODUCT UNITS

export const ProductUnitBaseSchema = z.object({
    code: z.string(),
    externalId: z.nullish(z.string()),
    categoryId: z.nullish(z.string()),
    categoryName: z.nullish(z.string()),
    readableSingularDe: z.string(),
    readablePluralDe: z.string(),
    readableSingularEn: z.string(),
    readablePluralEn: z.string(),
    integerOnly: z.nullish(z.boolean()),
});

// PRODUCT UNIT CONVERSIONS

export const ProductUnitConversionBaseSchema = z.object({
    productId: z.string(),
    fromCode: z.string(),
    toCode: z.string(),
    conversionFactor: z.number(),
});
export const ProductUnitConversionSchema = z.object({
    productId: z.string(),
    fromCode: z.string(),
    toCode: z.string(),
    conversionFactor: z.number(),
    orgId: z.string(),
    syncMarker: z.nullish(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    deletedAt: z.nullish(z.string()),
    blocked: z.nullish(z.boolean()),
    blockedAt: z.nullish(z.string()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
export const UpsertProductUnitConversionSchema = z.object({
    productId: z.string().check(z.minLength(1)),
    fromCode: z.string(),
    toCode: z.string(),
    conversionFactor: z.number(),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// PRODUCT PRICES

export const ProductPriceSchema = z.object({
    productId: z.string(),
    currencyId: z.string(),
    priceType: z.string(),
    validFrom: z.string(),
    validTo: z.nullish(z.string()),
    price: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
export const UpsertProductPriceSchema = z.object({
    productId: z.string().check(z.minLength(1)),
    currencyId: z.string().check(z.minLength(1)),
    priceType: z.string().check(z.minLength(1)),
    validFrom: z.string().check(z.minLength(1)),
    validTo: z._default(z.nullish(z.string()), null),
    price: z.number(),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// UNIT OF MEASURE
export const UnitOfMeasureSchema = z.object({
    code: z.string(),
    externalId: z.nullish(z.string()),
    categoryId: z.nullish(z.string()),
    categoryName: z.nullish(z.string()),
    readableSingularDe: z.string(),
    readablePluralDe: z.string(),
    readableSingularEn: z.string(),
    readablePluralEn: z.string(),
    integerOnly: z.boolean(),
    primary: z.boolean(),
    conversionFactors: z.record(z.string(), z.number()),
});

// PRODUCTS

export const ReplacementProductSchema = z.object({
    id: z.string(),
    code: z.string(),
    title: z.string(),
});

export const ProductSchema = z.object({
    id: z.string(),
    parentId: z.nullish(z.string()),
    code: z.string(),
    ean: z.nullish(z.string()),
    active: z.nullish(z.boolean()),
    weight: z.nullish(z.number()),
    weightUnit: z.nullish(z.string()),
    groupId1: z.nullish(z.string()),
    groupId2: z.nullish(z.string()),
    groupId3: z.nullish(z.string()),
    groupId4: z.nullish(z.string()),
    groupId5: z.nullish(z.string()),
    groupName1: z.nullish(z.string()),
    groupName2: z.nullish(z.string()),
    groupName3: z.nullish(z.string()),
    groupName4: z.nullish(z.string()),
    groupName5: z.nullish(z.string()),
    referenceProductId1: z.nullish(z.string()),
    referenceProductId2: z.nullish(z.string()),
    referenceProductId3: z.nullish(z.string()),
    materialName1: z.nullish(z.string()),
    materialName2: z.nullish(z.string()),
    materialName3: z.nullish(z.string()),
    replacesProductId: z.nullish(z.string()),
    replacesProduct: z.nullish(ReplacementProductSchema),
    replacedByProductId: z.nullish(z.string()),
    replacedByProduct: z.nullish(ReplacementProductSchema),
    titleDe1: z.string(),
    textDe1: z.nullish(z.string()),
    textDe2: z.nullish(z.string()),
    textDe3: z.nullish(z.string()),
    textDe4: z.nullish(z.string()),
    textDe5: z.nullish(z.string()),
    titleEn1: z.string(),
    textEn1: z.nullish(z.string()),
    textEn2: z.nullish(z.string()),
    textEn3: z.nullish(z.string()),
    textEn4: z.nullish(z.string()),
    textEn5: z.nullish(z.string()),
    titleFr1: z.string(),
    textFr1: z.nullish(z.string()),
    textFr2: z.nullish(z.string()),
    textFr3: z.nullish(z.string()),
    textFr4: z.nullish(z.string()),
    textFr5: z.nullish(z.string()),
    defaultSalesUnitCode: z.nullish(z.string()),
    defaultPriceUnitCode: z.string(),
    articleNumber1: z.nullish(z.string()),
    articleNumber2: z.nullish(z.string()),
    articleNumber3: z.nullish(z.string()),
    articleNumber4: z.nullish(z.string()),
    colorId: z.nullish(z.string()),
    color: z.nullish(z.string()),
    length: z.nullish(z.number()),
    lengthUnitId: z.nullish(z.string()),
    lengthUnit: z.nullish(z.string()),
    width: z.nullish(z.number()),
    widthUnitId: z.nullish(z.string()),
    widthUnit: z.nullish(z.string()),
    height: z.nullish(z.number()),
    heightUnitId: z.nullish(z.string()),
    heightUnit: z.nullish(z.string()),
    diameter: z.nullish(z.number()),
    diameterUnitId: z.nullish(z.string()),
    diameterUnit: z.nullish(z.string()),
    sizeId: z.nullish(z.string()),
    size: z.nullish(z.string()),
    prices: z.array(ProductPriceSchema),
    unitsOfMeasure: z.array(UnitOfMeasureSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    customerArticleNumbers: z.nullish(
        z.record(
            z.string(),
            z.array(
                z.object({
                    articleNumber: z.string(),
                    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
                }),
            ),
        ),
    ),
});
export const UpsertProductSchema = z.object({
    id: z.string().check(z.minLength(1)),
    parentId: z._default(z.nullish(z.string()), null),
    code: z.string().check(z.minLength(1)),
    ean: z._default(z.nullish(z.string()), null),
    active: z._default(z.nullish(z.boolean()), true),
    weight: z._default(z.nullish(z.number()), null),
    weightUnit: z._default(z.nullish(z.string().check(z.minLength(1))), null),
    groupId1: z._default(z.nullish(z.string()), null),
    groupId2: z._default(z.nullish(z.string()), null),
    groupId3: z._default(z.nullish(z.string()), null),
    groupId4: z._default(z.nullish(z.string()), null),
    groupId5: z._default(z.nullish(z.string()), null),
    groupName1: z._default(z.nullish(z.string()), null),
    groupName2: z._default(z.nullish(z.string()), null),
    groupName3: z._default(z.nullish(z.string()), null),
    groupName4: z._default(z.nullish(z.string()), null),
    groupName5: z._default(z.nullish(z.string()), null),
    referenceProductId1: z._default(z.nullish(z.string()), null),
    referenceProductId2: z._default(z.nullish(z.string()), null),
    referenceProductId3: z._default(z.nullish(z.string()), null),
    materialName1: z._default(z.nullish(z.string()), null),
    materialName2: z._default(z.nullish(z.string()), null),
    materialName3: z._default(z.nullish(z.string()), null),
    replacesProductId: z._default(z.nullish(z.string()), null),
    replacedByProductId: z._default(z.nullish(z.string()), null),
    titleDe1: z.string(),
    textDe1: z._default(z.nullish(z.string()), null),
    textDe2: z._default(z.nullish(z.string()), null),
    textDe3: z._default(z.nullish(z.string()), null),
    textDe4: z._default(z.nullish(z.string()), null),
    textDe5: z._default(z.nullish(z.string()), null),
    titleEn1: z.string(),
    textEn1: z._default(z.nullish(z.string()), null),
    textEn2: z._default(z.nullish(z.string()), null),
    textEn3: z._default(z.nullish(z.string()), null),
    textEn4: z._default(z.nullish(z.string()), null),
    textEn5: z._default(z.nullish(z.string()), null),
    titleFr1: z.string(),
    textFr1: z._default(z.nullish(z.string()), null),
    textFr2: z._default(z.nullish(z.string()), null),
    textFr3: z._default(z.nullish(z.string()), null),
    textFr4: z._default(z.nullish(z.string()), null),
    textFr5: z._default(z.nullish(z.string()), null),
    defaultSalesUnitCode: z._default(z.nullish(z.string()), null),
    defaultPriceUnitCode: z._default(z.nullish(z.string()), null),
    articleNumber1: z._default(z.nullish(z.string()), null),
    articleNumber2: z._default(z.nullish(z.string()), null),
    articleNumber3: z._default(z.nullish(z.string()), null),
    articleNumber4: z._default(z.nullish(z.string()), null),
    colorId: z._default(z.nullish(z.string()), null),
    color: z._default(z.nullish(z.string()), null),
    length: z._default(z.nullish(z.number()), null),
    lengthUnitId: z._default(z.nullish(z.string()), null),
    lengthUnit: z._default(z.nullish(z.string()), null),
    width: z._default(z.nullish(z.number()), null),
    widthUnitId: z._default(z.nullish(z.string()), null),
    widthUnit: z._default(z.nullish(z.string()), null),
    height: z._default(z.nullish(z.number()), null),
    heightUnitId: z._default(z.nullish(z.string()), null),
    heightUnit: z._default(z.nullish(z.string()), null),
    diameter: z._default(z.nullish(z.number()), null),
    diameterUnitId: z._default(z.nullish(z.string()), null),
    diameterUnit: z._default(z.nullish(z.string()), null),
    sizeId: z._default(z.nullish(z.string()), null),
    size: z._default(z.nullish(z.string()), null),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// PRICE LISTS

export const UpsertPriceListSchema = z.object({
    id: z.string().check(z.minLength(1)),
    titleDe: z.string().check(z.minLength(1)),
    titleEn: z.string().check(z.minLength(1)),
    titleFr: z.string().check(z.minLength(1)),
    globalDefault: z._default(z.nullish(z.boolean()), false),
    validFrom: z._default(z.nullish(z.string()), null),
    validTo: z._default(z.nullish(z.string()), null),
    currencyId: z._default(z.nullish(z.string()), null),
    priority: z._default(z.nullish(z.number()), 0),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
    blocked: z._default(z.nullish(z.boolean()), false),
});
export const UpsertPriceListEntrySchema = z.object({
    listId: z.string().check(z.minLength(1)),
    entryType: z.string().check(z.minLength(1)),
    productId: z.string().check(z.minLength(1)),
    validFrom: z.string().check(z.minLength(1)),
    validTo: z._default(z.nullish(z.string()), null),
    listValue: z.number(),
    minQuantity: z._default(z.nullish(z.number()), 0),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
    blocked: z._default(z.nullish(z.boolean()), false),
});
export const UpsertPriceListCustomerSchema = z.object({
    listId: z.string().check(z.minLength(1)),
    customerId: z.string().check(z.minLength(1)),
    validFrom: z.string().check(z.minLength(1)),
    validTo: z._default(z.nullish(z.string()), null),
    customerDefault: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
    blocked: z._default(z.nullish(z.boolean()), false),
});

// COUNTRIES

export const CountryBaseSchema = z.object({
    id: z.string(),
    iso2Code: z.nullish(z.string()),
    iso3Code: z.nullish(z.string()),
    nameEn: z.string(),
    nameDe: z.string(),
    nameFr: z.string(),
});
export const CountrySchema = z.object({
    id: z.string(),
    iso2Code: z.string(),
    iso3Code: z.string(),
    nameEn: z.string(),
    nameDe: z.string(),
    nameFr: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
export const UpsertCountrySchema = z.object({
    id: z.string().check(z.minLength(1)),
    iso2Code: z._default(z.nullish(z.string().check(z.length(2))), null),
    iso3Code: z._default(z.nullish(z.string().check(z.length(3))), null),
    nameEn: z.string().check(z.minLength(1)),
    nameDe: z.string().check(z.minLength(1)),
    nameFr: z.string().check(z.minLength(1)),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// ADDRESSES

export const AddressSchema = z.object({
    id: z.string(),
    sequenceNumber: z.nullish(z.string()),
    name1: z.string(),
    postalCode: z.string(),
    city: z.string(),
    countryId: z.string(),
    name2: z.nullish(z.string()),
    name3: z.nullish(z.string()),
    name4: z.nullish(z.string()),
    name5: z.nullish(z.string()),
    street1: z.nullish(z.string()),
    houseNumber1: z.nullish(z.string()),
    streetWithNumber1: z.nullish(z.string()),
    street2: z.nullish(z.string()),
    houseNumber2: z.nullish(z.string()),
    streetWithNumber2: z.nullish(z.string()),
    street3: z.nullish(z.string()),
    houseNumber3: z.nullish(z.string()),
    streetWithNumber3: z.nullish(z.string()),
    street4: z.nullish(z.string()),
    houseNumber4: z.nullish(z.string()),
    streetWithNumber4: z.nullish(z.string()),
    street5: z.nullish(z.string()),
    houseNumber5: z.nullish(z.string()),
    streetWithNumber5: z.nullish(z.string()),
    deliveryInstructions: z.nullish(z.array(z.string())),
    phone1: z.nullish(z.string()),
    phone2: z.nullish(z.string()),
    phone3: z.nullish(z.string()),
    fax1: z.nullish(z.string()),
    fax2: z.nullish(z.string()),
    fax3: z.nullish(z.string()),
    email1: z.nullish(z.string()),
    email2: z.nullish(z.string()),
    email3: z.nullish(z.string()),
    postBox: z.nullish(z.string()),
    county: z.nullish(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    country: z.nullish(CountrySchema),
});

export const AddressWithDefaultSchema = z.object({
    id: z.string(),
    sequenceNumber: z.nullish(z.string()),
    name1: z.string(),
    postalCode: z.string(),
    city: z.string(),
    countryId: z.string(),
    name2: z.nullish(z.string()),
    name3: z.nullish(z.string()),
    name4: z.nullish(z.string()),
    name5: z.nullish(z.string()),
    street1: z.nullish(z.string()),
    houseNumber1: z.nullish(z.string()),
    streetWithNumber1: z.nullish(z.string()),
    street2: z.nullish(z.string()),
    houseNumber2: z.nullish(z.string()),
    streetWithNumber2: z.nullish(z.string()),
    street3: z.nullish(z.string()),
    houseNumber3: z.nullish(z.string()),
    streetWithNumber3: z.nullish(z.string()),
    street4: z.nullish(z.string()),
    houseNumber4: z.nullish(z.string()),
    streetWithNumber4: z.nullish(z.string()),
    street5: z.nullish(z.string()),
    houseNumber5: z.nullish(z.string()),
    streetWithNumber5: z.nullish(z.string()),
    deliveryInstructions: z.nullish(z.array(z.string())),
    phone1: z.nullish(z.string()),
    phone2: z.nullish(z.string()),
    phone3: z.nullish(z.string()),
    fax1: z.nullish(z.string()),
    fax2: z.nullish(z.string()),
    fax3: z.nullish(z.string()),
    email1: z.nullish(z.string()),
    email2: z.nullish(z.string()),
    email3: z.nullish(z.string()),
    postBox: z.nullish(z.string()),
    county: z.nullish(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    country: z.nullish(CountrySchema),
    isDefault: z.boolean(),
});
export const UpsertAddressSchema = z.object({
    id: z.string().check(z.minLength(1)),
    sequenceNumber: z._default(z.nullish(z.string()), null),
    name1: z.string(),
    postalCode: z.string(),
    city: z.string(),
    countryId: z.string().check(z.minLength(1)),
    name2: z._default(z.nullish(z.string()), null),
    name3: z._default(z.nullish(z.string()), null),
    name4: z._default(z.nullish(z.string()), null),
    name5: z._default(z.nullish(z.string()), null),
    street1: z._default(z.nullish(z.string()), null),
    houseNumber1: z._default(z.nullish(z.string()), null),
    streetWithNumber1: z._default(z.nullish(z.string()), null),
    street2: z._default(z.nullish(z.string()), null),
    houseNumber2: z._default(z.nullish(z.string()), null),
    streetWithNumber2: z._default(z.nullish(z.string()), null),
    street3: z._default(z.nullish(z.string()), null),
    houseNumber3: z._default(z.nullish(z.string()), null),
    streetWithNumber3: z._default(z.nullish(z.string()), null),
    street4: z._default(z.nullish(z.string()), null),
    houseNumber4: z._default(z.nullish(z.string()), null),
    streetWithNumber4: z._default(z.nullish(z.string()), null),
    street5: z._default(z.nullish(z.string()), null),
    houseNumber5: z._default(z.nullish(z.string()), null),
    streetWithNumber5: z._default(z.nullish(z.string()), null),
    deliveryInstructions: z._default(z.nullish(z.array(z.string())), null),
    phone1: z._default(z.nullish(z.string()), null),
    phone2: z._default(z.nullish(z.string()), null),
    phone3: z._default(z.nullish(z.string()), null),
    fax1: z._default(z.nullish(z.string()), null),
    fax2: z._default(z.nullish(z.string()), null),
    fax3: z._default(z.nullish(z.string()), null),
    email1: z._default(z.nullish(z.string()), null),
    email2: z._default(z.nullish(z.string()), null),
    email3: z._default(z.nullish(z.string()), null),
    postBox: z._default(z.nullish(z.string()), null),
    county: z._default(z.nullish(z.string()), null),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// PARTNERS

export const PartnerSchema = z.object({
    id: z.string(),
    isIntercompany: z.nullish(z.boolean()),
    erpCompanyId: z.nullish(z.number()),
    name1: z.nullish(z.string()),
    name2: z.nullish(z.string()),
    name3: z.nullish(z.string()),
    partnerNumber: z.nullish(z.string()),
    partnerSlug: z.nullish(z.string()),
    taxNumber: z.nullish(z.string()),
    domains: z.nullish(z.array(z.string())),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
export const PartnerWithAddressesSchema = z.object({
    id: z.string(),
    isIntercompany: z.nullish(z.boolean()),
    erpCompanyId: z.nullish(z.number()),
    name1: z.nullish(z.string()),
    name2: z.nullish(z.string()),
    name3: z.nullish(z.string()),
    partnerNumber: z.nullish(z.string()),
    partnerSlug: z.nullish(z.string()),
    taxNumber: z.nullish(z.string()),
    domains: z.nullish(z.array(z.string())),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    billingAddresses: z.array(AddressWithDefaultSchema),
    deliveryAddresses: z.array(AddressWithDefaultSchema),
    mainAddresses: z.array(AddressWithDefaultSchema),
});
export const UpsertPartnerSchema = z.object({
    id: z.string().check(z.minLength(1)),
    isIntercompany: z.nullish(z.boolean()),
    erpCompanyId: z._default(z.nullish(z.number()), null),
    name1: z._default(z.nullish(z.string()), null),
    name2: z._default(z.nullish(z.string()), null),
    name3: z._default(z.nullish(z.string()), null),
    partnerNumber: z._default(z.nullish(z.string()), null),
    partnerSlug: z._default(z.nullish(z.string()), null),
    taxNumber: z._default(z.nullish(z.string()), null),
    domains: z._default(z.nullish(z.array(z.string())), null),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});
export const UpsertPartnerAddressSchema = z.object({
    partnerId: z.string().check(z.minLength(1)),
    addressId: z.string().check(z.minLength(1)),
    addressType: z.enum(["INVOICE", "DELIVERY", "MAIN"]),
    isDefault: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
    blocked: z._default(z.nullish(z.boolean()), false),
});

// CUSTOMER ACCOUNTS

export const CustomerAccountBaseSchema = z.object({
    id: z.string(),
    partnerId: z.string(),
    unionMemberId: z.nullish(z.string()),
    defaultDeliveryTerms: z.nullish(z.string()),
    defaultShippingTerms: z.nullish(z.string()),
    defaultPaymentTerms: z.nullish(z.string()),
    defaultCustomTerms1: z.nullish(z.string()),
    defaultCustomTerms2: z.nullish(z.string()),
    defaultCustomTerms3: z.nullish(z.string()),
    defaultBillingPartnerId: z.string(),
    defaultShippingPartnerId: z.string(),
    defaultPayingPartnerId: z.string(),
    popupText: z.nullish(z.string()),
});
export const CustomerAccountSchema = z.object({
    id: z.string(),
    partnerId: z.string(),
    unionMemberId: z.nullish(z.string()),
    defaultDeliveryTerms: z.nullish(z.string()),
    defaultShippingTerms: z.nullish(z.string()),
    defaultPaymentTerms: z.nullish(z.string()),
    defaultCustomTerms1: z.nullish(z.string()),
    defaultCustomTerms2: z.nullish(z.string()),
    defaultCustomTerms3: z.nullish(z.string()),
    defaultBillingPartnerId: z.string(),
    defaultShippingPartnerId: z.string(),
    defaultPayingPartnerId: z.string(),
    creditLimit: z.nullish(z.number()),
    popupText: z.nullish(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    partner: z.nullish(PartnerWithAddressesSchema),
    defaultBillingPartner: z.nullish(PartnerWithAddressesSchema),
    defaultShippingPartner: z.nullish(PartnerWithAddressesSchema),
    defaultPayingPartner: z.nullish(PartnerWithAddressesSchema),
});
export const UpsertCustomerAccountSchema = z.object({
    id: z.string().check(z.minLength(1)),
    partnerId: z.string().check(z.minLength(1)),
    unionMemberId: z._default(z.nullish(z.string()), null),
    defaultDeliveryTerms: z._default(z.nullish(z.string()), null),
    defaultShippingTerms: z._default(z.nullish(z.string()), null),
    defaultPaymentTerms: z._default(z.nullish(z.string()), null),
    defaultCustomTerms1: z._default(z.nullish(z.string()), null),
    defaultCustomTerms2: z._default(z.nullish(z.string()), null),
    defaultCustomTerms3: z._default(z.nullish(z.string()), null),
    defaultBillingPartnerId: z.string().check(z.minLength(1)),
    defaultShippingPartnerId: z.string().check(z.minLength(1)),
    defaultPayingPartnerId: z.string().check(z.minLength(1)),
    creditLimit: z._default(z.nullish(z.number()), null),
    popupText: z._default(z.nullish(z.string()), null),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// SELLING ENTITIES
export const SellingEntitySchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
export const UpsertSellingEntitySchema = z.object({
    id: z.string().check(z.minLength(1)),
    code: z.string().check(z.minLength(1)),
    name: z.string().check(z.minLength(1)),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

export const SellingEntityCustomerAccountSchema = z.object({
    sellingEntityId: z.string(),
    customerAccountId: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
export const UpsertSellingEntityCustomerAccountSchema = z.object({
    sellingEntityId: z.string().check(z.minLength(1)),
    customerAccountId: z.string().check(z.minLength(1)),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
    blocked: z._default(z.nullish(z.boolean()), false),
});

export const UpsertDictionarySchema = z.object({
    dictionaryName: z.string(),
    valueId: z.string(),
    value1: z._default(z.nullish(z.string()), null),
    value2: z._default(z.nullish(z.string()), null),
    value3: z._default(z.nullish(z.string()), null),
    value4: z._default(z.nullish(z.string()), null),
    value5: z._default(z.nullish(z.string()), null),
    valueJson1: z._default(z.nullish(SerializableSchema), null),
    valueJson2: z._default(z.nullish(SerializableSchema), null),
    valueJson3: z._default(z.nullish(SerializableSchema), null),
    valueJson4: z._default(z.nullish(SerializableSchema), null),
    valueJson5: z._default(z.nullish(SerializableSchema), null),
});

export const SellingEntityPartnerSchema = z.object({
    sellingEntityId: z.string(),
    partnerId: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
export const UpsertSellingEntityPartnerSchema = z.object({
    sellingEntityId: z.string().check(z.minLength(1)),
    partnerId: z.string().check(z.minLength(1)),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
    blocked: z._default(z.nullish(z.boolean()), false),
});

export const SellingEntityAddressSchema = z.object({
    sellingEntityId: z.string(),
    addressId: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
export const UpsertSellingEntityAddressSchema = z.object({
    sellingEntityId: z.string().check(z.minLength(1)),
    addressId: z.string().check(z.minLength(1)),
    isDefault: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
    blocked: z._default(z.nullish(z.boolean()), false),
});

// CUSTOMER ARTICLE NUMBERS

export const CustomerArticleNumberSchema = z.object({
    productId: z.string(),
    customerId: z.string(),
    articleNumber: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    product: z.nullish(ProductSchema),
    customer: z.nullish(CustomerAccountSchema),
});

export const UpsertCustomerArticleNumberSchema = z.object({
    productId: z.string().check(z.minLength(1)),
    customerId: z.string().check(z.minLength(1)),
    articleNumber: z.string().check(z.minLength(1)),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// CURRENCIES

export const CurrencySchema = z.object({
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    blocked: z.nullish(z.boolean()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    id: z.string(),
    isoCode: z.string(),
});
export const UpsertCurrencySchema = z.object({
    id: z.string().check(z.minLength(1)),
    isoCode: z.string().check(z.length(3)),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// PRODUCT UNITS

export const ProductUnitSchema = z.object({
    code: z.string(),
    externalId: z.nullish(z.string()),
    categoryId: z.nullish(z.string()),
    categoryName: z.nullish(z.string()),
    readableSingularDe: z.string(),
    readablePluralDe: z.string(),
    readableSingularEn: z.string(),
    readablePluralEn: z.string(),
    integerOnly: z.nullish(z.boolean()),
    orgId: z.string(),
    syncMarker: z.nullish(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    deletedAt: z.nullish(z.string()),
    blocked: z.nullish(z.boolean()),
    blockedAt: z.nullish(z.string()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
});
export const UpsertProductUnitSchema = z.object({
    code: z.string().check(z.minLength(1)),
    externalId: z._default(z.nullish(z.string()), null),
    categoryId: z._default(z.nullish(z.string()), null),
    categoryName: z._default(z.nullish(z.string()), null),
    readableSingularDe: z.string().check(z.minLength(1)),
    readablePluralDe: z.string().check(z.minLength(1)),
    readableSingularEn: z.string().check(z.minLength(1)),
    readablePluralEn: z.string().check(z.minLength(1)),
    integerOnly: z._default(z.nullish(z.boolean()), false),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

export const KoestnerSearchSchema = z.object({
    orgId: OrganizationIdSchema,
    strict: z.nullish(z.boolean()),
    articleNumbers: z.nullish(z.array(z.string())),
    length: z.nullish(z.number()),
    width: z.nullish(z.number()),
    strength: z.nullish(z.number()),
    otherDimensions: z.array(z.number()),
    productType: z.nullish(z.string()),
    productMaterialCode: z.nullish(z.string()),
    productNorm: z.nullish(z.string()),
    limit: z.number(),
});

// SUBMITTED TRANSACTIONS

export const PositionCustomFieldsSchema = z.record(z.string(), SerializableSchema);

export const SubmittedTransactionIdSchema = EntityIdSchema("stx");

export const SubmittedTransactionSchema = z.object({
    id: SubmittedTransactionIdSchema,
    orgId: OrganizationIdSchema,
    suggestedTransactionId: z.string(),
    erpTransactionId: z.string(),
    erpTransactionNumber: z.string(),
    threadId: ThreadIdSchema,
    customerAccountId: z.string(),
    shippingTerms: z.nullish(z.string()),
    paymentTerms: z.nullish(z.string()),
    billToPartyId: z.string(),
    billToAddressId: z.string(),
    shipToPartyId: z.string(),
    shipToAddressId: z.string(),
    sellingEntityId: z.nullish(z.string()),
    sellingEntityAddressId: z.nullish(z.string()),
    newBilledPartyAddress: z.nullish(ExtractedAddressSchema),
    newPayingPartyAddress: z.nullish(ExtractedAddressSchema),
    newShipToAddress: z.nullish(ExtractedAddressSchema),
    receivedDate: z.string(),
    emailReceivedDate: z._default(z.nullish(z.string()), null),
    entryDate: z._default(z.nullish(z.string()), null),
    orderDate: z.string(),
    deliveryType: z.nullish(DeliveryTypeSchema),
    contactId: z.nullish(z.string()),
    contactName: z.string(),
    contactEmail: z.string(),
    contactPhone: z.string(),
    orderReference: z.string(),
    quoteNumber: z.nullable(z.string()),
    comment: z.nullable(z.string()),
    transactionType: z.string(),
    currencyId: z.string(),
    currencyCode: z.string(),
    deletedPositionIds: z.array(z.string()),
    erpResponse: z.nullish(z.string()),
    customFields: z.nullish(PositionCustomFieldsSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdBy: z.string(),
});

export const CreateSubmittedTransactionSchema = z.object({
    id: SubmittedTransactionIdSchema,
    shippingTerms: z.nullable(z.string()),
    paymentTerms: z.nullable(z.string()),
    orgId: OrganizationIdSchema,
    suggestedTransactionId: z.string(),
    erpTransactionId: z.string(),
    erpTransactionNumber: z.string(),
    threadId: ThreadIdSchema,
    customerAccountId: z.string(),
    billToPartyId: z.string(),
    billToAddressId: z.string(),
    shipToPartyId: z.string(),
    shipToAddressId: z.string(),
    sellingEntityId: z._default(z.nullish(z.string()), null),
    sellingEntityAddressId: z._default(z.nullish(z.string()), null),
    newBilledPartyAddress: z.nullish(ExtractedAddressSchema),
    newPayingPartyAddress: z.nullish(ExtractedAddressSchema),
    newShipToAddress: z.nullish(ExtractedAddressSchema),
    receivedDate: z.string(),
    emailReceivedDate: z._default(z.nullish(z.string()), null),
    entryDate: z._default(z.nullish(z.string()), null),
    deliveryType: z._default(z.nullish(DeliveryTypeSchema), null),
    orderDate: z.string(),
    contactId: z._default(z.nullish(z.string()), null),
    contactName: z.string(),
    contactEmail: z.string(),
    contactPhone: z.string(),
    orderReference: z.string(),
    quoteNumber: z.nullable(z.string()),
    comment: z.string(),
    transactionType: z.string(),
    currencyId: z.string(),
    currencyCode: z.string(),
    deletedPositionIds: z._default(z.array(z.string()), []),
    erpResponse: z._default(z.nullish(z.string()), null),
    customFields: z._default(z.nullish(PositionCustomFieldsSchema), null),
    createdBy: z.string(),
});

// SUBMITTED POSITIONS

export const SubmittedPositionIdSchema = EntityIdSchema("spo");

export const SubmittedPositionSchema = z.object({
    id: SubmittedPositionIdSchema,
    orgId: OrganizationIdSchema,
    threadId: ThreadIdSchema,
    transactionId: z.string(),
    suggestedPositionId: z.string(),
    selectedMatchId: z.string(),
    added: z.boolean(),
    positionIndex: z.number(),
    productId: z.string(),
    quantity: z.number(),
    quantityUnitId: z.string(),
    quantityUnitCode: z.string(),
    price: z.number(),
    priceUnitId: z.string(),
    priceUnitCode: z.string(),
    priceQuantity: z.number(),
    deliveryDate: z.nullish(z.string()),
    externalPositionNumber: z.nullish(z.string()),
    customFields: z.nullish(PositionCustomFieldsSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
});

export const CreateSubmittedPositionSchema = z.object({
    id: SubmittedPositionIdSchema,
    orgId: OrganizationIdSchema,
    threadId: ThreadIdSchema,
    transactionId: SubmittedTransactionIdSchema,
    suggestedPositionId: z.string(),
    selectedMatchId: z.string(),
    added: z.boolean(),
    positionIndex: z.number(),
    productId: z.string(),
    quantity: z.number(),
    quantityUnitId: z.string(),
    quantityUnitCode: z.string(),
    price: z.number(),
    priceUnitId: z.string(),
    priceUnitCode: z.string(),
    priceQuantity: z.number(),
    deliveryDate: z._default(z.nullish(z.string()), null),
    externalPositionNumber: z._default(z.nullish(z.string()), null),
    customFields: z._default(z.nullish(PositionCustomFieldsSchema), null),
});

export const SubmitTransactionInputSchema = z.omit(CreateSubmittedTransactionSchema, {
    id: true,
    orgId: true,
    erpTransactionId: true,
    erpTransactionNumber: true,
    erpResponse: true,
    createdBy: true,
});

export const SubmitPositionInputSchema = z.omit(CreateSubmittedPositionSchema, {
    id: true,
    orgId: true,
    threadId: true,
    transactionId: true,
});

export const ShippingTermsSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    orgId: z.string(),
    syncMarker: z.nullable(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullable(z.boolean()),
    blocked: z.nullable(z.boolean()),
    customFields: z.nullable(z.record(z.string(), SerializableSchema)),
});

export const UpsertShippingTermsSchema = z.object({
    id: z.string().check(z.minLength(1)),
    code: z.string(),
    name: z.string().check(z.minLength(1)),
    syncMarker: z.nullish(z.string()),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

export const PaymentTermsSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    orgId: z.string(),
    syncMarker: z.nullable(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullable(z.boolean()),
    blocked: z.nullable(z.boolean()),
    customFields: z.nullable(z.record(z.string(), SerializableSchema)),
});

export const UpsertPaymentTermsSchema = z.object({
    id: z.string().check(z.minLength(1)),
    code: z.string().check(z.minLength(1)),
    name: z.string().check(z.minLength(1)),
    syncMarker: z.nullish(z.string()),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

export const InsertProcessLogSchema = z.object({
    orgId: OrganizationIdSchema,
    suggestedTransactionId: z.nullable(TransactionIdSchema),
    submittedTransactionId: z.nullable(SubmittedTransactionIdSchema),
    threadId: z.nullable(ThreadIdSchema),
    logKey: z.string(),
    logValue: SerializableSchema,
});

export const SalesOrderDBDictionarySchema = z.object({
    orgId: OrganizationIdSchema,
    dictionaryName: z.string(),
    valueId: z.string(),
    value1: z.nullable(z.string()),
    value2: z.nullable(z.string()),
    value3: z.nullable(z.string()),
    value4: z.nullable(z.string()),
    value5: z.nullable(z.string()),
    valueJson1: SerializableSchema,
    valueJson2: SerializableSchema,
    valueJson3: SerializableSchema,
    valueJson4: SerializableSchema,
    valueJson5: SerializableSchema,
    createdAt: z.nullable(z.string()),
    updatedAt: z.nullable(z.string()),
    deleted: z.boolean(),
    deletedAt: z.nullable(z.string()),
    blocked: z.boolean(),
});

// EMAIL FOLDER CHANGES

export const MoveTriggerSchema = z.enum(["AUTOMATIC", "MANUAL"]);

export const EmailFolderChangeSchema = z.object({
    id: z.number(),
    orgId: OrganizationIdSchema,
    inboxId: InboxIdSchema,
    threadId: ThreadIdSchema,
    suggestedTransactionId: TransactionIdSchema,
    messageIds: z.array(MessageIdSchema),
    parentFolderId: z.string(),
    targetFolder: z.string(),
    triggerAt: z.string(),
    movedAt: z.nullable(z.string()),
    moveTrigger: z.nullable(MoveTriggerSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.nullable(z.string()),
    failedAt: z.nullable(z.string()),
    failReason: z.nullable(z.string()),
});

export const CreateEmailFolderChangeSchema = z.object({
    orgId: OrganizationIdSchema,
    inboxId: InboxIdSchema,
    threadId: ThreadIdSchema,
    suggestedTransactionId: TransactionIdSchema,
    messageIds: z.array(MessageIdSchema),
    parentFolderId: z.string(),
    targetFolder: z.string(),
    triggerAt: z.string(),
});

// EMPLOYEES

export const EmployeeSchema = z.object({
    orgId: z.string(),
    id: z.string(),
    code: z.string(),
    name: z.nullish(z.string()),
    firstName: z.nullish(z.string()),
    lastName: z.nullish(z.string()),
    email: z.nullish(z.string()),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    deletedAt: z.nullish(z.string()),
    blocked: z.nullish(z.boolean()),
    blockedAt: z.nullish(z.string()),
    syncMarker: z.nullish(z.string()),
});

export const UpsertEmployeeSchema = z.object({
    id: z.string().check(z.minLength(1)),
    code: z.string().check(z.minLength(1)),
    name: z._default(z.nullish(z.string()), null),
    firstName: z._default(z.nullish(z.string()), null),
    lastName: z._default(z.nullish(z.string()), null),
    email: z._default(z.nullish(z.string()), null),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

// EMPLOYEE CUSTOMERS

export const EmployeeCustomerSchema = z.object({
    orgId: z.string(),
    customerId: z.string(),
    employeeId: z.string(),
    customFields: z.nullish(z.record(z.string(), SerializableSchema)),
    createdAt: z.string(),
    updatedAt: z.string(),
    deleted: z.nullish(z.boolean()),
    deletedAt: z.nullish(z.string()),
    blocked: z.nullish(z.boolean()),
    blockedAt: z.nullish(z.string()),
    syncMarker: z.nullish(z.string()),
});

export const UpsertEmployeeCustomerSchema = z.object({
    customerId: z.string().check(z.minLength(1)),
    employeeId: z.string().check(z.minLength(1)),
    deleted: z._default(z.nullish(z.boolean()), false),
    blocked: z._default(z.nullish(z.boolean()), false),
    customFields: z._default(z.nullish(z.record(z.string(), SerializableSchema)), null),
});

export const SalesOrderDictionarySchema = z.object({
    orgId: OrganizationIdSchema,
    name: z.string(),
    entries: z.record(
        z.string(),
        z.object({
            id: z.string(),
            value1: z.nullable(z.string()),
            value2: z.nullable(z.string()),
            value3: z.nullable(z.string()),
            value4: z.nullable(z.string()),
            value5: z.nullable(z.string()),
            valueJson1: SerializableSchema,
            valueJson2: SerializableSchema,
            valueJson3: SerializableSchema,
            valueJson4: SerializableSchema,
            valueJson5: SerializableSchema,
        }),
    ),
});
